/**
 * Fallback UI shown when the React tree crashes. Wrapped by Sentry's
 * ErrorBoundary in App.tsx so the user sees a calm "something went wrong,
 * here's a button" screen instead of a blank white page.
 *
 * Brand voice: don't blame the user, don't pretend nothing happened, give
 * them a single obvious action. The "Reload" button calls the hard-reset
 * helper installed by main.tsx, which clears the service worker and
 * caches before reloading — this is the same path the chunk-failure
 * handler uses, so a real bad-shell will recover here too.
 */

import { Button } from "@/components/ui/button";
import { RefreshCw, Mail } from "lucide-react";

interface Props {
  /** Sentry passes the error and a reset callback. We accept them but the
   *  reload button is more useful than a soft reset for a real crash. */
  error?: unknown;
  resetError?: () => void;
}

export const ErrorFallback = ({ error, resetError }: Props) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
      ? error
      : "Something went wrong.";
  const stack =
    error instanceof Error && typeof error.stack === "string"
      ? error.stack
      : "";

  // Diagnostic helper: pre-fill the support mailto with the actual error
  // message + stack so we don't have to dig in Xcode device logs to
  // reproduce a crash. Sentry receives the same info but if the user
  // hasn't connected Sentry, this is the only out-of-band channel we have.
  const mailtoHref = (() => {
    const subject = encodeURIComponent("Pleasant crashed");
    const body = encodeURIComponent(
      `Error: ${message}\n\nStack:\n${stack ? stack.slice(0, 1800) : "(no stack)"}\n\n---\nUserAgent: ${typeof navigator !== "undefined" ? navigator.userAgent : "unknown"}`,
    );
    return `mailto:support@pleasantroutes.com?subject=${subject}&body=${body}`;
  })();

  const hardReset = () => {
    const w = window as unknown as { __pleasantHardReset?: () => void };
    if (typeof w.__pleasantHardReset === "function") {
      w.__pleasantHardReset();
    } else {
      window.location.reload();
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-hero px-6 py-12">
      <div className="w-full max-w-md text-center">
        <h1 className="heading text-3xl sm:text-4xl mb-3">Something went wrong.</h1>
        <p className="text-muted-foreground leading-relaxed mb-6">
          Pleasant hit an unexpected error. Reloading usually fixes it. If it
          keeps happening, email us — we read every report.
        </p>

        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Button
            onClick={hardReset}
            className="h-12 rounded-2xl bg-pleasant text-primary-foreground gap-2 px-5"
          >
            <RefreshCw className="w-4 h-4" /> Reload
          </Button>
          {resetError && (
            <Button
              onClick={resetError}
              variant="outline"
              className="h-12 rounded-2xl gap-2 px-5"
            >
              Try again
            </Button>
          )}
          <Button
            asChild
            variant="ghost"
            className="h-12 rounded-2xl gap-2 px-5 text-muted-foreground"
          >
            <a href={mailtoHref}>
              <Mail className="w-4 h-4" /> Email support
            </a>
          </Button>
        </div>

        {/* TEMPORARY (#187): show error details in production too. We're
         *  hunting a fresh-install crash with no Sentry DSN configured —
         *  the only signal we get is what the user reads on screen. Revert
         *  to dev-only once the crash is fixed. */}
        <details className="mt-8 text-left text-xs text-muted-foreground/80" open>
          <summary className="cursor-pointer">Technical details</summary>
          <pre className="mt-2 whitespace-pre-wrap break-words bg-muted/40 rounded-lg p-3 text-[11px] leading-snug">
            {message}
            {stack ? `\n\n${stack.slice(0, 2000)}` : ""}
          </pre>
        </details>
      </div>
    </main>
  );
};
